import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { PageHeader, StatusPill } from "@/components/admin/PageHeader";
import {
  addTenantDomainFn,
  addTenantMemberFn,
  createTenantFn,
  listTenantsFn,
  removeTenantDomainFn,
  removeTenantMemberFn,
  setTenantStatusFn,
} from "@/lib/tenant/tenant.functions";
import { CB16_TENANT_ID } from "@/lib/tenant/constants";

export const Route = createFileRoute("/admin/tenants")({
  head: () => ({
    meta: [
      { title: "Businesses — CB16 Admin" },
      { name: "description", content: "Super Admin management of businesses, website addresses and staff." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Businesses — CB16 Admin" },
      { property: "og:description", content: "Super Admin management of businesses on the platform." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: TenantsPage,
});

const input = "w-full border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#C6A15B]";
const btn = "border border-black bg-black px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-white hover:bg-black/85 disabled:opacity-50";
const ghost = "text-[11px] font-semibold uppercase tracking-[0.18em] text-black/60 hover:text-[#C6A15B] disabled:opacity-50";

function errMsg(e: unknown) {
  return e instanceof Error ? e.message.replace(/^Error:\s*/, "") : "Something went wrong";
}

function TenantsPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["admin", "tenants"], queryFn: () => listTenantsFn() });
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin", "tenants"] });
  const run = (fn: () => Promise<unknown>, ok: string) =>
    fn().then(() => { toast.success(ok); return refresh(); }).catch((e) => toast.error(errMsg(e)));

  const [form, setForm] = useState({ name: "", slug: "", orderPrefix: "", hostname: "", isTest: true });
  const create = useMutation({
    mutationFn: () => createTenantFn({ data: form }),
    onSuccess: () => { toast.success("Business created"); setForm({ name: "", slug: "", orderPrefix: "", hostname: "", isTest: true }); void refresh(); },
    onError: (e) => toast.error(errMsg(e)),
  });

  if (q.isError) {
    return (
      <div className="flex flex-col gap-5">
        <PageHeader title="Businesses" subtitle="Super Admin only" />
        <p className="text-sm text-black/70">{errMsg(q.error)}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Businesses" subtitle="Each business has its own products, orders, content, settings and staff. CB16 is the first business." />

      <section className="border border-black/10 bg-white p-5">
        <h3 className="text-sm font-semibold uppercase tracking-[0.18em]">Add a business</h3>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="text-xs text-black/60">Name<input className={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
          <label className="text-xs text-black/60">Short name (slug)<input className={input} value={form.slug} placeholder="e.g. santmira" onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })} /></label>
          <label className="text-xs text-black/60">Order prefix<input className={input} value={form.orderPrefix} placeholder="e.g. SM" onChange={(e) => setForm({ ...form, orderPrefix: e.target.value.toUpperCase() })} /></label>
          <label className="text-xs text-black/60">Website address (optional)<input className={input} value={form.hostname} placeholder="shop.example.com" onChange={(e) => setForm({ ...form, hostname: e.target.value })} /></label>
        </div>
        <div className="mt-4 flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.isTest} onChange={(e) => setForm({ ...form, isTest: e.target.checked })} />Test business</label>
          <button className={btn} disabled={create.isPending || !form.name || !form.slug || !form.orderPrefix} onClick={() => create.mutate()}>
            {create.isPending ? "Creating…" : "Create business"}
          </button>
        </div>
      </section>

      {q.isLoading && <p className="text-sm text-black/60">Loading businesses…</p>}
      {q.data?.tenants.map((t) => (
        <TenantCard key={t.id} tenant={t} roles={q.data.roles} run={run} />
      ))}
    </div>
  );
}

type Tenant = Awaited<ReturnType<typeof listTenantsFn>>["tenants"][number];

function TenantCard({ tenant: t, roles, run }: { tenant: Tenant; roles: string[]; run: (fn: () => Promise<unknown>, ok: string) => Promise<unknown> }) {
  const isCb16 = t.id === CB16_TENANT_ID;
  const [host, setHost] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("admin");
  return (
    <section className="border border-black/10 bg-white p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold">{t.name}</h3>
          <p className="text-xs text-black/60">{t.slug} · orders {t.orderPrefix}-… · added {new Date(t.createdAt).toLocaleDateString("en-IN")}</p>
        </div>
        <div className="flex items-center gap-2">
          {t.isTest && <StatusPill tone="info">Test</StatusPill>}
          <StatusPill tone={t.status === "active" ? "success" : "warn"}>{t.status}</StatusPill>
          {!isCb16 && (
            <button className={ghost} onClick={() => run(() => setTenantStatusFn({ data: { tenantId: t.id, status: t.status === "active" ? "suspended" : "active" } }), "Status updated")}>
              {t.status === "active" ? "Suspend" : "Reactivate"}
            </button>
          )}
        </div>
      </div>

      <div className="mt-4 grid gap-6 lg:grid-cols-2">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-black/50">Website addresses</p>
          <ul className="mt-2 divide-y divide-black/5 text-sm">
            {t.domains.map((d) => (
              <li key={d.id} className="flex items-center justify-between py-1.5">
                <span>{d.hostname}{d.isPrimary ? " (primary)" : ""}</span>
                {!isCb16 && <button className={ghost} onClick={() => run(() => removeTenantDomainFn({ data: { domainId: d.id } }), "Address removed")}>Remove</button>}
              </li>
            ))}
            {t.domains.length === 0 && <li className="py-1.5 text-black/50">None yet</li>}
          </ul>
          {!isCb16 && (
            <div className="mt-2 flex gap-2">
              <input className={input} value={host} placeholder="shop.example.com" onChange={(e) => setHost(e.target.value)} />
              <button className={btn} disabled={!host} onClick={() => run(() => addTenantDomainFn({ data: { tenantId: t.id, hostname: host } }), "Address added").then(() => setHost(""))}>Add</button>
            </div>
          )}
        </div>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-black/50">Staff</p>
          <ul className="mt-2 divide-y divide-black/5 text-sm">
            {t.members.map((m) => (
              <li key={m.id} className="flex items-center justify-between py-1.5">
                <span>{m.email ?? m.userId} · {m.role.replace(/_/g, " ")}</span>
                {!isCb16 && <button className={ghost} onClick={() => run(() => removeTenantMemberFn({ data: { membershipId: m.id } }), "Staff removed")}>Remove</button>}
              </li>
            ))}
            {t.members.length === 0 && <li className="py-1.5 text-black/50">None yet</li>}
          </ul>
          {isCb16 ? (
            <p className="mt-2 text-xs text-black/50">CB16 staff are managed in Users &amp; Roles.</p>
          ) : (
            <div className="mt-2 flex gap-2">
              <input className={input} value={email} placeholder="staff@example.com" onChange={(e) => setEmail(e.target.value)} />
              <select className={input + " max-w-[160px]"} value={role} onChange={(e) => setRole(e.target.value)}>
                {roles.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
              </select>
              <button className={btn} disabled={!email} onClick={() => run(() => addTenantMemberFn({ data: { tenantId: t.id, email, role } }), "Staff added").then(() => setEmail(""))}>Add</button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
